// src/pages/AcademiaPage.js
import React, { useState, useEffect } from 'react';

const ds = { fontFamily: "'Cormorant Garamond', Georgia, serif" };
const bs = { fontFamily: "'Montserrat', sans-serif" };

const DIAS = ['Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'];

// Paleta de colores por disciplina — elegante, coherente con el sitio
const DISC_PALETTE = [
  { bg: '#fdf2f2', accent: '#961914', text: '#7a140f' },
  { bg: '#f0f4ff', accent: '#3b5bdb', text: '#2c4bc9' },
  { bg: '#f0fdf6', accent: '#1a7a4a', text: '#166040' },
  { bg: '#fdf8f0', accent: '#b45309', text: '#92400e' },
  { bg: '#f5f0ff', accent: '#6d28d9', text: '#5b21b6' },
  { bg: '#f0fdfd', accent: '#0e7490', text: '#0c6177' },
];

const SectionLabel = ({ children }) => (
  <p style={{ ...bs, color: '#961914', fontSize: '10px', letterSpacing: '0.38em',
    textTransform: 'uppercase', marginBottom: '10px', fontWeight: 600, textAlign: 'center' }}>
    {children}
  </p>
);
const SectionTitle = ({ children }) => (
  <h2 style={{ ...ds, fontSize: 'clamp(1.9rem,3.5vw,2.8rem)', fontWeight: 400,
    color: '#060D14', lineHeight: 1.15, marginBottom: '8px', textAlign: 'center' }}>
    {children}
  </h2>
);
const Divider = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '10px', marginBottom: '32px' }}>
    <div style={{ height: '1px', width: '36px', background: '#e5c9c9' }} />
    <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#961914' }} />
    <div style={{ height: '1px', width: '36px', background: '#e5c9c9' }} />
  </div>
);

const timeToMin = (t) => {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
};
const fmtHora = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  return `${parseInt(h)}:${m || '00'}`;
};

// ── CALENDARIO ─────────────────────────────────────────────────────────────
const CalendarioSemanal = ({ horarios, discPalette, onAgendar }) => {
  const [diaActivo, setDiaActivo] = useState(null);

  const conDia = horarios.filter(h => h.dia_semana && h.hora_inicio);
  const sinDia = horarios.filter(h => !h.dia_semana || !h.hora_inicio);
  const diasConHorario = DIAS.filter(d => conDia.some(h => h.dia_semana === d));

  // En móvil mostrar un día a la vez
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const diaSeleccionado = diaActivo || (diasConHorario[0] || null);

  // Calcular rango solo para el día activo — sin horas vacías
  const clasesDelDia = conDia.filter(h => h.dia_semana === diaSeleccionado);
  const minsDelDia   = clasesDelDia.flatMap(h =>
    [timeToMin(h.hora_inicio), timeToMin(h.hora_fin)].filter(Boolean)
  );
  const minHour = minsDelDia.length ? Math.floor(Math.min(...minsDelDia) / 60) : 8;
  const maxHour = minsDelDia.length ? Math.ceil(Math.max(...minsDelDia) / 60) : 21;
  const hours   = Array.from({ length: maxHour - minHour }, (_, i) => minHour + i);

  const CELL_H  = 60;
  const LABEL_W = 44;

  if (!diasConHorario.length && !sinDia.length) return (
    <p style={{ ...bs, textAlign: 'center', color: '#bbb', fontSize: '13px', padding: '24px 0' }}>
      Próximamente los horarios.
    </p>
  );

  return (
    <div>
      {/* ── Leyenda de instrucción ─────────────────────────────────────── */}
      {conDia.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: '10px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px',
            background: '#fdf2f2', border: '1px solid #f5c9c9',
            borderRadius: '2px', padding: '10px 18px' }}>
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#961914">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"/>
            </svg>
            <span style={{ ...bs, fontSize: '11px', color: '#961914', fontWeight: 600,
              letterSpacing: '0.05em' }}>
              Toca cualquier clase para agendar tu prueba gratuita
            </span>
          </div>
        </div>
      )}

      {/* ── Selector de día (mobile tabs) ─────────────────────────────── */}
      {diasConHorario.length > 0 && (
        <div style={{ overflowX: 'auto', marginBottom: '16px' }}>
          <div style={{ display: 'flex', gap: '6px', minWidth: 'max-content',
            padding: '0 2px' }}>
            {diasConHorario.map(dia => {
              const active = diaSeleccionado === dia;
              return (
                <button key={dia} onClick={() => setDiaActivo(dia)}
                  style={{ ...bs, background: active ? '#961914' : 'white',
                    color: active ? 'white' : '#888',
                    border: `1px solid ${active ? '#961914' : '#e5e0e0'}`,
                    padding: '8px 16px', cursor: 'pointer', fontSize: '11px',
                    fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase',
                    borderRadius: '2px', transition: 'all 0.2s', whiteSpace: 'nowrap',
                  }}
                  onMouseOver={e => { if (!active) { e.currentTarget.style.borderColor = '#961914'; e.currentTarget.style.color = '#961914'; }}}
                  onMouseOut={e => { if (!active) { e.currentTarget.style.borderColor = '#e5e0e0'; e.currentTarget.style.color = '#888'; }}}>
                  {dia}
                  <span style={{ marginLeft: '6px', opacity: 0.6, fontSize: '10px' }}>
                    ({conDia.filter(h => h.dia_semana === dia).length})
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Vista de día seleccionado ──────────────────────────────────── */}
      {diaSeleccionado && (
        <div style={{ background: 'white', border: '1px solid #f0eaea',
          borderRadius: '2px', overflow: 'hidden' }}>

          {/* Header día */}
          <div style={{ background: '#060D14', padding: '14px 20px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ ...ds, color: 'white', fontSize: '1.4rem',
              fontWeight: 400, margin: 0, letterSpacing: '0.05em' }}>
              {diaSeleccionado}
            </h3>
            <span style={{ ...bs, color: 'rgba(255,255,255,0.4)', fontSize: '10px',
              letterSpacing: '0.15em', textTransform: 'uppercase' }}>
              {conDia.filter(h => h.dia_semana === diaSeleccionado).length} clase{conDia.filter(h => h.dia_semana === diaSeleccionado).length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Grid hora × clases */}
          <div style={{ position: 'relative', overflowX: 'auto' }}>
            <div style={{ minWidth: '280px', position: 'relative' }}>

              {/* Filas de hora */}
              {hours.map((hour, hi) => (
                <div key={hour} style={{
                  display: 'flex', height: `${CELL_H}px`,
                  borderBottom: hi < hours.length - 1 ? '1px solid #f8f5f5' : 'none',
                }}>
                  {/* Etiqueta hora */}
                  <div style={{ width: `${LABEL_W}px`, flexShrink: 0,
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
                    padding: '8px 10px 0 0', background: '#faf9f9',
                    borderRight: '1px solid #f0eaea' }}>
                    <span style={{ ...bs, fontSize: '10px', color: '#ccc', fontWeight: 600 }}>
                      {hour}:00
                    </span>
                  </div>
                  {/* Área de clases */}
                  <div style={{ flex: 1, position: 'relative' }} />
                </div>
              ))}

              {/* Clases superpuestas */}
              {clasesDelDia.map(h => {
                  const startMin = timeToMin(h.hora_inicio);
                  const endMin   = timeToMin(h.hora_fin) || startMin + 60;
                  const top      = (startMin - minHour * 60) / 60 * CELL_H;
                  const height   = Math.max((endMin - startMin) / 60 * CELL_H - 6, 40);

                  const discIdx = discPalette[h.disciplina] ?? 0;
                  const c       = DISC_PALETTE[discIdx % DISC_PALETTE.length];

                  return (
                    <div key={h.id}
                      onClick={() => onAgendar(h)}
                      style={{
                        position: 'absolute',
                        top: `${top}px`,
                        left: `${LABEL_W + 8}px`,
                        right: '8px',
                        height: `${height}px`,
                        background: c.bg,
                        borderLeft: `3px solid ${c.accent}`,
                        borderRadius: '2px',
                        padding: '8px 12px',
                        cursor: 'pointer',
                        boxSizing: 'border-box',
                        transition: 'all 0.2s',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: '2px',
                      }}
                      onMouseOver={e => {
                        e.currentTarget.style.boxShadow = `0 6px 20px rgba(0,0,0,0.12)`;
                        e.currentTarget.style.transform = 'translateX(2px)';
                      }}
                      onMouseOut={e => {
                        e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}>

                      {/* Hora */}
                      <p style={{ ...bs, fontSize: '9px', fontWeight: 700,
                        color: c.text, margin: 0, letterSpacing: '0.05em' }}>
                        {fmtHora(h.hora_inicio)}{h.hora_fin ? ` – ${fmtHora(h.hora_fin)}` : ''}
                      </p>

                      {/* Disciplina */}
                      {h.disciplina && (
                        <p style={{ ...ds, fontSize: '1rem', fontWeight: 600,
                          color: '#060D14', margin: 0, lineHeight: 1.2 }}>
                          {h.disciplina}
                        </p>
                      )}

                      {/* Nivel */}
                      {h.nivel && height > 50 && (
                        <p style={{ ...bs, fontSize: '10px', color: '#888',
                          margin: 0, fontWeight: 500 }}>
                          {h.nivel}
                        </p>
                      )}

                      {/* Maestro con foto */}
                      {h.maestro && height > 70 && (
                        <div style={{ display: 'flex', alignItems: 'center',
                          gap: '5px', marginTop: '2px' }}>
                          {h.maestro_foto && (
                            <img src={h.maestro_foto} alt={h.maestro}
                              style={{ width: '16px', height: '16px', borderRadius: '50%',
                                objectFit: 'cover', flexShrink: 0,
                                border: `1px solid ${c.accent}` }} />
                          )}
                          <span style={{ ...bs, fontSize: '9px', color: '#aaa',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {h.maestro}
                          </span>
                        </div>
                      )}

                      {/* Indicador click */}
                      <div style={{ position: 'absolute', bottom: '6px', right: '8px' }}>
                        <span style={{ ...bs, fontSize: '8px', color: c.accent,
                          fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
                          opacity: 0.7 }}>
                          + Agendar
                        </span>
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {/* ── Leyenda disciplinas ────────────────────────────────────────── */}
      {conDia.length > 0 && (
        <div style={{ marginTop: '16px', display: 'flex', flexWrap: 'wrap',
          gap: '8px', alignItems: 'center' }}>
          {[...new Map(
            conDia.filter(h => h.disciplina)
              .map(h => [h.disciplina, { disc: h.disciplina, idx: discPalette[h.disciplina] ?? 0 }])
          ).values()].map(({ disc, idx }) => {
            const c = DISC_PALETTE[idx % DISC_PALETTE.length];
            return (
              <span key={disc} style={{ ...bs, fontSize: '10px', fontWeight: 600,
                letterSpacing: '0.1em', padding: '4px 12px',
                background: c.bg, color: c.text,
                borderLeft: `3px solid ${c.accent}`,
                borderRadius: '1px' }}>
                {disc}
              </span>
            );
          })}
        </div>
      )}

      {/* ── Horarios sin día/hora (texto libre) ───────────────────────── */}
      {sinDia.length > 0 && (
        <div style={{ marginTop: conDia.length ? '24px' : '0' }}>
          <p style={{ ...bs, fontSize: '10px', letterSpacing: '0.2em', color: '#bbb',
            textTransform: 'uppercase', marginBottom: '10px' }}>
            Otros horarios
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {sinDia.map(h => (
              <div key={h.id} onClick={() => onAgendar(h)}
                style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', background: 'white',
                  border: '1px solid #f0eaea', borderRadius: '2px',
                  padding: '14px 20px', cursor: 'pointer', transition: 'all 0.2s' }}
                onMouseOver={e => { e.currentTarget.style.borderColor = '#e5c9c9'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(150,25,20,0.08)'; }}
                onMouseOut={e => { e.currentTarget.style.borderColor = '#f0eaea'; e.currentTarget.style.boxShadow = 'none'; }}>
                <div>
                  {h.disciplina && (
                    <span style={{ ...bs, fontSize: '10px', fontWeight: 600,
                      color: '#961914', textTransform: 'uppercase',
                      letterSpacing: '0.12em' }}>{h.disciplina} · </span>
                  )}
                  <span style={{ ...bs, fontSize: '13px', color: '#060D14' }}>
                    {h.horario || 'Ver horario'}
                  </span>
                  {h.nivel && (
                    <span style={{ ...bs, fontSize: '11px', color: '#bbb',
                      marginLeft: '8px' }}>{h.nivel}</span>
                  )}
                </div>
                <span style={{ ...bs, fontSize: '9px', fontWeight: 700,
                  color: '#961914', letterSpacing: '0.15em',
                  textTransform: 'uppercase', whiteSpace: 'nowrap', marginLeft: '12px' }}>
                  + Agendar prueba
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── MODAL AGENDAR ──────────────────────────────────────────────────────────
const ModalAgendar = ({ horario, onClose }) => {
  const [form,       setForm]       = useState({ nombre_completo: '', fecha_nacimiento: '', contacto: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done,       setDone]       = useState(false);

  useEffect(() => {
    const esc = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res  = await fetch('/api/create_clase_prueba.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ horario_id: horario.id, ...form }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Error al agendar');
      setDone(true);
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inp = {
    ...bs, width: '100%', padding: '11px 14px',
    border: '1px solid #e5e0e0', fontSize: '13px', color: '#060D14',
    outline: 'none', borderRadius: '1px', boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  };
  const lbl = {
    ...bs, display: 'block', fontSize: '10px', letterSpacing: '0.18em',
    textTransform: 'uppercase', color: '#999', marginBottom: '6px',
  };

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 300,
        background: 'rgba(6,13,20,0.72)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>

      <div style={{ background: 'white', width: '100%', maxWidth: '440px',
        borderRadius: '2px', boxShadow: '0 32px 80px rgba(0,0,0,0.25)',
        overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ background: '#961914', padding: '20px 24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <p style={{ ...bs, color: 'rgba(255,255,255,0.55)', fontSize: '10px',
                letterSpacing: '0.2em', textTransform: 'uppercase', margin: '0 0 4px' }}>
                Clase de Prueba Gratuita
              </p>
              <h3 style={{ ...ds, color: 'white', fontSize: '1.6rem', fontWeight: 600, margin: '0 0 8px' }}>
                {horario.disciplina || 'Clase General'}
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {horario.dia_semana && (
                  <span style={{ ...bs, background: 'rgba(255,255,255,0.15)', color: 'white',
                    fontSize: '11px', padding: '3px 10px', borderRadius: '20px' }}>
                    {horario.dia_semana}
                  </span>
                )}
                {(horario.hora_inicio || horario.hora_fin) && (
                  <span style={{ ...bs, background: 'rgba(255,255,255,0.15)', color: 'white',
                    fontSize: '11px', padding: '3px 10px', borderRadius: '20px' }}>
                    {fmtHora(horario.hora_inicio)}{horario.hora_fin ? ` – ${fmtHora(horario.hora_fin)}` : ''}
                  </span>
                )}
                {horario.nivel && (
                  <span style={{ ...bs, background: 'rgba(255,255,255,0.15)', color: 'white',
                    fontSize: '11px', padding: '3px 10px', borderRadius: '20px' }}>
                    {horario.nivel}
                  </span>
                )}
              </div>
              {horario.maestro && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginTop: '10px' }}>
                  {horario.maestro_foto && (
                    <img src={horario.maestro_foto} alt={horario.maestro}
                      style={{ width: '24px', height: '24px', borderRadius: '50%',
                        objectFit: 'cover', border: '2px solid rgba(255,255,255,0.4)' }} />
                  )}
                  <span style={{ ...bs, color: 'rgba(255,255,255,0.7)', fontSize: '12px' }}>
                    {horario.maestro}
                  </span>
                </div>
              )}
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none',
              cursor: 'pointer', color: 'rgba(255,255,255,0.6)', padding: '2px', flexShrink: 0 }}
            onMouseOver={e => e.currentTarget.style.color = 'white'}
            onMouseOut={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}>
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '24px' }}>
          {done ? (
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <div style={{ width: '56px', height: '56px', borderRadius: '50%',
                background: '#f0fdf4', border: '2px solid #86efac',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 16px', fontSize: '24px' }}>✓</div>
              <h3 style={{ ...ds, fontSize: '1.6rem', color: '#060D14', marginBottom: '8px' }}>
                ¡Solicitud enviada!
              </h3>
              <p style={{ ...bs, fontSize: '13px', color: '#aaa', lineHeight: 1.7, marginBottom: '20px' }}>
                Nos pondremos en contacto contigo para confirmar tu clase de prueba gratuita.
              </p>
              <button onClick={onClose} style={{ ...bs, background: 'none',
                border: '1px solid #e5e0e0', color: '#666', padding: '10px 24px',
                cursor: 'pointer', fontSize: '11px', fontWeight: 600,
                letterSpacing: '0.15em', textTransform: 'uppercase', borderRadius: '1px' }}>
                Cerrar
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={lbl}>Nombre Completo *</label>
                <input type="text" required value={form.nombre_completo}
                  onChange={e => setForm(p => ({ ...p, nombre_completo: e.target.value }))}
                  style={inp}
                  onFocus={e => e.target.style.borderColor = '#961914'}
                  onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
              </div>
              <div>
                <label style={lbl}>Fecha de Nacimiento *</label>
                <input type="date" required value={form.fecha_nacimiento}
                  onChange={e => setForm(p => ({ ...p, fecha_nacimiento: e.target.value }))}
                  style={inp}
                  onFocus={e => e.target.style.borderColor = '#961914'}
                  onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
              </div>
              <div>
                <label style={lbl}>WhatsApp / Teléfono *</label>
                <input type="tel" required value={form.contacto}
                  onChange={e => setForm(p => ({ ...p, contacto: e.target.value }))}
                  placeholder="55 1234 5678"
                  style={inp}
                  onFocus={e => e.target.style.borderColor = '#961914'}
                  onBlur={e => e.target.style.borderColor = '#e5e0e0'} />
              </div>
              <button type="submit" disabled={submitting} style={{
                ...bs, padding: '14px', background: '#961914', color: 'white',
                border: 'none', cursor: submitting ? 'not-allowed' : 'pointer',
                fontSize: '11px', fontWeight: 600, letterSpacing: '0.18em',
                textTransform: 'uppercase', borderRadius: '1px',
                opacity: submitting ? 0.7 : 1, transition: 'background 0.2s', marginTop: '4px',
              }}
              onMouseOver={e => { if (!submitting) e.currentTarget.style.background = '#7a140f'; }}
              onMouseOut={e => e.currentTarget.style.background = '#961914'}>
                {submitting ? 'Enviando…' : 'Solicitar Clase de Prueba'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// ── COMPONENTE PRINCIPAL ───────────────────────────────────────────────────
const AcademiaPage = () => {
  const [data,         setData]         = useState(null);
  const [loaded,       setLoaded]       = useState(false);
  const [modalHorario, setModalHorario] = useState(null);

  // Mapa disciplina → índice de color (estable por nombre)
  const [discPalette, setDiscPalette] = useState({});

  useEffect(() => {
    fetch('/api/get_academia_data.php')
      .then(r => r.json())
      .then(j => {
        if (!j.error) {
          setData(j);
          // Asignar colores estables a cada disciplina
          const map = {};
          (j.disciplinas || []).forEach((d, i) => { map[d.nombre] = i; });
          // También capturar disciplinas que vienen solo en horarios
          (j.horarios || []).forEach(h => {
            if (h.disciplina && map[h.disciplina] === undefined) {
              map[h.disciplina] = Object.keys(map).length;
            }
          });
          setDiscPalette(map);
        }
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) return (
    <section id="academia-section" style={{ padding: '80px 24px', background: '#faf9f9' }}>
      <p style={{ ...bs, textAlign: 'center', color: '#bbb', fontSize: '12px',
        letterSpacing: '0.15em', textTransform: 'uppercase' }}>
        Cargando Academia…
      </p>
    </section>
  );

  if (!data?.id) return (
    <section id="academia-section" style={{ padding: '80px 24px', background: '#faf9f9' }}>
      <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
        <SectionLabel>Academia</SectionLabel>
        <SectionTitle>Nuestra Academia</SectionTitle>
        <p style={{ ...bs, color: '#aaa', fontSize: '13px', lineHeight: 1.7 }}>
          Próximamente información. ¡Contáctanos para más detalles!
        </p>
      </div>
    </section>
  );

  const { disciplinas = [], niveles = [], maestros = [], horarios = [], costos = [] } = data;
  const isEmpty = !disciplinas.length && !niveles.length && !maestros.length
                  && !horarios.length && !costos.length;

  // Secciones activas para calcular índice de franja alternada
  const secciones = [
    disciplinas.length > 0 && 'disciplinas',
    niveles.length > 0     && 'niveles',
    maestros.length > 0    && 'maestros',
    horarios.length > 0    && 'horarios',
    costos.length > 0      && 'costos',
  ].filter(Boolean);

  // Fondos alternados: blanco / crema / blanco / crema...
  const fondos = ['#ffffff', '#faf7f4'];
  const fondo = (nombre) => fondos[secciones.indexOf(nombre) % 2];

  const SeccionWrapper = ({ nombre, label, title, children }) => (
    <section style={{ background: fondo(nombre), padding: 'clamp(56px,7vw,88px) 24px' }}>
      <div style={{ maxWidth: '960px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <SectionLabel>{label}</SectionLabel>
          <SectionTitle>{title}</SectionTitle>
          <Divider />
        </div>
        {children}
      </div>
    </section>
  );

  return (
    <>
      {/* Header de academia */}
      <section id="academia-section" style={{ background: '#060D14',
        padding: 'clamp(48px,6vw,72px) 24px', textAlign: 'center' }}>
        <p style={{ ...bs, color: 'rgba(255,255,255,0.4)', fontSize: '10px',
          letterSpacing: '0.38em', textTransform: 'uppercase',
          marginBottom: '10px', fontWeight: 600 }}>
          Academia Eledé
        </p>
        <h2 style={{ ...ds, fontSize: 'clamp(2rem,4vw,3.2rem)', fontWeight: 300,
          color: 'white', lineHeight: 1.15, margin: '0 0 16px' }}>
          Nuestra Academia
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
          <div style={{ height: '1px', width: '36px', background: 'rgba(255,255,255,0.2)' }} />
          <div style={{ width: '4px', height: '4px', borderRadius: '50%',
            background: 'rgba(255,255,255,0.4)' }} />
          <div style={{ height: '1px', width: '36px', background: 'rgba(255,255,255,0.2)' }} />
        </div>
      </section>

      {isEmpty && (
        <section style={{ background: '#faf7f4', padding: '64px 24px', textAlign: 'center' }}>
          <p style={{ ...bs, color: '#bbb', fontSize: '13px' }}>
            Estamos preparando el contenido. ¡Vuelve pronto!
          </p>
        </section>
      )}

      {/* Disciplinas */}
      {disciplinas.length > 0 && (
        <SeccionWrapper nombre="disciplinas" label="Lo que ofrecemos" title="Disciplinas">
          <div style={{ display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: '12px' }}>
            {disciplinas.map((d, i) => {
              const c = DISC_PALETTE[i % DISC_PALETTE.length];
              return (
                <div key={d.id} style={{ padding: '20px 16px', textAlign: 'center',
                  background: c.bg, borderLeft: `3px solid ${c.accent}`,
                  position: 'relative', overflow: 'hidden',
                  transition: 'box-shadow 0.3s', cursor: 'default' }}
                onMouseOver={e => e.currentTarget.style.boxShadow = '0 6px 24px rgba(0,0,0,0.08)'}
                onMouseOut={e => e.currentTarget.style.boxShadow = 'none'}>
                  <span style={{ ...ds, position: 'absolute', top: '-6px', right: '8px',
                    fontSize: '48px', fontWeight: 300, color: 'rgba(0,0,0,0.04)',
                    lineHeight: 1, userSelect: 'none' }}>
                    {String(i+1).padStart(2,'0')}
                  </span>
                  <p style={{ ...ds, fontSize: '1.15rem', fontWeight: 600,
                    color: '#060D14', position: 'relative', margin: 0 }}>
                    {d.nombre}
                  </p>
                </div>
              );
            })}
          </div>
        </SeccionWrapper>
      )}

      {/* Niveles */}
      {niveles.length > 0 && (
        <SeccionWrapper nombre="niveles" label="Para todos" title="Niveles">
          <div style={{ display: 'flex', flexWrap: 'wrap',
            justifyContent: 'center', gap: '10px' }}>
            {niveles.map(n => (
              <span key={n.id} style={{ ...bs, padding: '8px 22px',
                border: '1px solid #e5c9c9', color: '#961914', fontSize: '11px',
                fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase',
                background: 'white', transition: 'all 0.2s', cursor: 'default' }}
              onMouseOver={e => { e.currentTarget.style.background = '#961914'; e.currentTarget.style.color = 'white'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = '#961914'; }}>
                {n.nombre}
              </span>
            ))}
          </div>
        </SeccionWrapper>
      )}

      {/* Maestros */}
      {maestros.length > 0 && (
        <SeccionWrapper nombre="maestros" label="Nuestro equipo" title="Maestros">
          <div style={{ display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '20px' }}>
            {maestros.map(m => (
              <div key={m.id} style={{
                background: 'white', border: '1px solid #f0eaea',
                padding: '24px', transition: 'box-shadow 0.3s' }}
              onMouseOver={e => e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.06)'}
              onMouseOut={e => e.currentTarget.style.boxShadow = 'none'}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '12px' }}>
                  <div style={{ width: '56px', height: '56px', borderRadius: '50%',
                    overflow: 'hidden', background: '#f5f2f2', flexShrink: 0,
                    border: '2px solid #e5c9c9' }}>
                    {m.foto_url
                      ? <img src={m.foto_url} alt={m.nombre}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <div style={{ width: '100%', height: '100%', display: 'flex',
                          alignItems: 'center', justifyContent: 'center', color: '#ddd' }}>
                          <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                          </svg>
                        </div>
                    }
                  </div>
                  <div>
                    <div style={{ width: '28px', height: '2px', background: '#961914', marginBottom: '6px' }} />
                    <h4 style={{ ...ds, fontSize: '1.2rem', fontWeight: 600,
                      color: '#060D14', margin: 0 }}>{m.nombre}</h4>
                  </div>
                </div>
                {m.descripcion && (
                  <p style={{ ...bs, color: '#999', fontSize: '12px',
                    lineHeight: 1.75, margin: 0 }}>{m.descripcion}</p>
                )}
              </div>
            ))}
          </div>
        </SeccionWrapper>
      )}

      {/* Horarios */}
      {horarios.length > 0 && (
        <SeccionWrapper nombre="horarios" label="Planifica tu semana" title="Horarios">
          <CalendarioSemanal
            horarios={horarios}
            discPalette={discPalette}
            onAgendar={setModalHorario}
          />
        </SeccionWrapper>
      )}

      {/* Costos */}
      {costos.length > 0 && (
        <SeccionWrapper nombre="costos" label="Inversión" title="Costos">
          <div style={{ maxWidth: '560px', margin: '0 auto',
            display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {costos.map((c, i) => (
              <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', padding: '16px 24px',
                background: i % 2 === 0 ? 'white' : '#fdfafa',
                border: '1px solid #f0eaea', transition: 'border-color 0.2s,box-shadow 0.2s' }}
              onMouseOver={e => { e.currentTarget.style.borderColor = '#e5c9c9'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(150,25,20,0.06)'; }}
              onMouseOut={e => { e.currentTarget.style.borderColor = '#f0eaea'; e.currentTarget.style.boxShadow = 'none'; }}>
                <span style={{ ...bs, fontSize: '13px', color: '#060D14', fontWeight: 500 }}>
                  {c.descripcion}
                </span>
                <span style={{ ...ds, fontSize: '1.4rem', color: '#961914',
                  fontWeight: 400, whiteSpace: 'nowrap', marginLeft: '16px' }}>
                  ${Number(c.monto).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </SeccionWrapper>
      )}

      {modalHorario && (
        <ModalAgendar horario={modalHorario} onClose={() => setModalHorario(null)} />
      )}
    </>
  );
};

export default AcademiaPage;
